'use client'

import { memo } from 'react'
import { Clock } from 'lucide-react'
import { BaseNode } from './BaseNode'
import type { TimeCheckData } from '@/lib/pbx/flow/types'

interface TimeCheckNodeProps {
  data: TimeCheckData & { label?: string }
  selected?: boolean
}

// Day abbreviations map (Tu/Th to distinguish Tuesday/Thursday)
const DAY_SHORT: Record<string, string> = {
  Mon: 'M', Tue: 'Tu', Wed: 'W', Thu: 'Th', Fri: 'F', Sat: 'Sa', Sun: 'Su',
}

// Format days compactly
function formatDays(days: string[] | undefined): string {
  if (!days || days.length === 0) return 'Not set'

  // Check common patterns first
  const sorted = [...days].sort((a, b) => {
    const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    return order.indexOf(a) - order.indexOf(b)
  })
  const key = sorted.join(',')

  if (key === 'Mon,Tue,Wed,Thu,Fri') return 'Mon-Fri'
  if (key === 'Mon,Tue,Wed,Thu,Fri,Sat,Sun') return 'All days'
  if (key === 'Sat,Sun') return 'Weekends'

  // Convert to short form
  return days.map(d => DAY_SHORT[d] || d.slice(0, 2)).join(' ')
}

// Format timezone compactly
function formatTimezone(tz: string | undefined): string {
  if (!tz) return 'UTC'
  // Extract just the city/region name
  const parts = tz.split('/')
  return parts[parts.length - 1].replace('_', ' ')
}

function TimeCheckNodeComponent({ data, selected }: TimeCheckNodeProps) {
  const schedule = data?.schedule?.default
  const days = formatDays(schedule?.days)
  const time = schedule?.time_ranges?.[0]
    ? `${schedule.time_ranges[0].start}-${schedule.time_ranges[0].end}`
    : 'Not set'
  const timezone = formatTimezone(data?.timezone)

  return (
    <BaseNode
      icon={<Clock className="w-4 h-4" />}
      label="Time Check"
      selected={selected}
      outputHandles={[
        { id: 'working_hours', label: 'Open', type: 'working_hours' },
        { id: 'after_hours', label: 'Closed', type: 'after_hours' },
      ]}
      className="border-yellow-500/50 bg-yellow-50/50 dark:bg-yellow-950/20"
    >
      <div className="text-[10px] text-muted-foreground leading-tight">
        <span>{days}</span>
        <span className="mx-1">·</span>
        <span>{time}</span>
        <div className="mt-0.5 opacity-70">{timezone}</div>
      </div>
    </BaseNode>
  )
}

export const TimeCheckNode = memo(TimeCheckNodeComponent)
